import type {
  OperatorCostReport,
  OperatorHealthReport,
  OperatorQualityReport,
  OperatorUsageReport,
} from "@esi/shared";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  BarChart,
  formatBytes,
  formatNumber,
  formatUsd,
  StatGrid,
  StatTile,
} from "@/components/charts";
import { Notice } from "@/components/Notice";
import { Page } from "@/components/Page";
import { PageHeader } from "@/components/PageHeader";
import { Skeleton } from "@/components/ui/skeleton";
import { FigureTable, Freshness, Section } from "../admin/reports/parts.tsx";
import { api } from "../api.ts";
import { ok } from "../api-errors.ts";

interface Figures {
  usage: OperatorUsageReport;
  quality: OperatorQualityReport;
  cost: OperatorCostReport;
  health: OperatorHealthReport;
}

/**
 * Instance reports (spec `usage-reporting`, operator API): per-tenant totals only, the last 30
 * days. No entity, project or user of any tenant is ever named here.
 */
export function ConsoleReports() {
  const { t } = useTranslation();
  const reports = useQuery({
    queryKey: ["console", "reports"],
    queryFn: async (): Promise<Figures> => {
      const r = api.api.operator.reports;
      const [usage, quality, cost, health] = await Promise.all([
        r.usage.$get({ query: {} }).then(ok),
        r.quality.$get({ query: {} }).then(ok),
        r.cost.$get({ query: {} }).then(ok),
        r.health.$get({ query: {} }).then(ok),
      ]);
      return { usage, quality, cost, health };
    },
    retry: false,
    staleTime: 60_000,
  });
  return (
    <Page width="wide">
      <PageHeader title={t("console.reports.title")} lead={t("console.reports.lead")} />
      {reports.isError ? (
        <Notice tone="danger">{reports.error.message}</Notice>
      ) : !reports.data ? (
        <Skeleton className="h-72 w-full" />
      ) : (
        <ReportBody figures={reports.data} />
      )}
    </Page>
  );
}

function ReportBody({ figures }: { figures: Figures }) {
  const { t } = useTranslation();
  const { usage, quality, cost, health } = figures;
  const questions = usage.tenants.reduce((n, x) => n + x.totals.questions, 0);
  const spend = cost.tenants.reduce((n, x) => n + x.totals.costUsd, 0);
  const failures = health.tenants.reduce((n, x) => n + x.totals.ingestionFailures, 0);
  const byId = <T extends { tenantId: string }>(rows: readonly T[]) =>
    new Map(rows.map((r) => [r.tenantId, r]));
  const costs = byId(cost.tenants);
  const qualities = byId(quality.tenants);
  const healths = byId(health.tenants);
  // A tenant's row mixes the four reports (its month spend comes from the cost report, which
  // flags this month's synthetic spend): it is marked when any of them flags it.
  const synthetic = new Set(
    [usage, quality, cost, health].flatMap((r) =>
      r.tenants.filter((x) => x.synthetic).map((x) => x.tenantId),
    ),
  );
  return (
    <div className="flex flex-col gap-8">
      <Freshness freshness={usage.freshness} synthetic={synthetic.size > 0} />
      <StatGrid>
        <StatTile label={t("console.reports.tenants")} value={formatNumber(usage.tenants.length)} />
        <StatTile label={t("console.reports.questions")} value={formatNumber(questions)} />
        <StatTile label={t("console.reports.cost")} value={formatUsd(spend)} />
        <StatTile label={t("console.reports.failures")} value={formatNumber(failures)} />
      </StatGrid>
      <BarChart
        title={t("console.reports.costByTenant")}
        summary={t("console.reports.costSummary", { total: formatUsd(spend) })}
        data={cost.tenants
          .map((x) => ({ label: x.name, value: x.totals.costUsd }))
          .sort((a, b) => b.value - a.value)
          .slice(0, 15)}
        formatValue={formatUsd}
        labelHeader={t("console.reports.tenant")}
        valueHeader={t("console.reports.cost")}
      />
      <Section title={t("console.reports.perTenant")}>
        <FigureTable
          caption={t("console.reports.perTenant")}
          columns={[
            t("console.reports.tenant"),
            t("console.reports.questions"),
            t("console.reports.conversations"),
            t("console.reports.cost"),
            t("console.reports.mcpCost"),
            t("console.reports.monthSpend"),
            t("console.reports.storage"),
            t("console.reports.unsupported"),
            t("console.reports.failures"),
          ]}
          rows={usage.tenants.map((x) => {
            const c = costs.get(x.tenantId);
            return {
              key: x.tenantId,
              cells: [
                // Marked in words: its figures include synthetic demo data (demo-world-v2).
                synthetic.has(x.tenantId)
                  ? t("console.reports.syntheticTenant", { name: x.name })
                  : x.name,
                formatNumber(x.totals.questions),
                formatNumber(x.totals.conversations),
                formatUsd(x.totals.costUsd),
                mcpCost(c),
                c ? formatUsd(c.monthSpend.usd) : "—",
                c ? formatBytes(c.storageBytes) : "—",
                formatNumber(qualities.get(x.tenantId)?.totals.unsupported ?? 0),
                formatNumber(healths.get(x.tenantId)?.totals.ingestionFailures ?? 0),
              ],
            };
          })}
        />
      </Section>
      {cost.instance ? <InstanceCosts instance={cost.instance} /> : null}
    </div>
  );
}

/**
 * The instance's own spend, attributed to no tenant (#410): the feedback triage assistant today,
 * this calendar month and over the period. A dash when an older API sends the period alone.
 */
function InstanceCosts({ instance }: { instance: NonNullable<OperatorCostReport["instance"]> }) {
  const { t } = useTranslation();
  const triage = instance.feedbackTriage;
  return (
    <Section title={t("console.reports.instanceCosts")}>
      <FigureTable
        caption={t("console.reports.instanceCosts")}
        columns={[
          t("console.reports.instanceLine"),
          t("console.reports.today"),
          t("console.reports.thisMonth"),
          t("console.reports.last30Days"),
        ]}
        rows={[
          {
            key: "feedback-triage",
            cells: [
              t("console.reports.feedbackTriage"),
              triage.today ? formatUsd(triage.today.usd) : "—",
              triage.month ? formatUsd(triage.month.usd) : "—",
              formatUsd(triage.usd),
            ],
          },
        ]}
      />
    </Section>
  );
}

/** A tenant's MCP line: "—" when the cost report predates origins (canary) or is missing. */
function mcpCost(cost: OperatorCostReport["tenants"][number] | undefined): string {
  if (!cost?.origins) {
    return "—";
  }
  return formatUsd(cost.origins.find((o) => o.origin === "mcp")?.costUsd ?? 0);
}
